import { useState } from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Delete, GraphicEq, PlayCircleOutline } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import type { ObservationMedia } from '../../types/observationMedia';

interface ObservationMediaCardProps {
  item: ObservationMedia;
  canEdit: boolean;
  onOpen: (item: ObservationMedia) => void;
  onDelete: (item: ObservationMedia) => void;
  onCaptionSave: (item: ObservationMedia, caption: string | null) => void;
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** One media attachment tile — photo thumbnail, video poster, or audio player. */
export const ObservationMediaCard = ({ item, canEdit, onOpen, onDelete, onCaptionSave }: ObservationMediaCardProps) => {
  const theme = useTheme();
  const [caption, setCaption] = useState(item.Caption ?? '');

  const preview = () => {
    if (item.MediaType === 'photo') {
      return (
        <Box
          component="img"
          src={item.thumbnailUrl || item.mediaUrl}
          alt={item.FileName}
          loading="lazy"
          onClick={() => onOpen(item)}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            // Thumbnail may lag the upload by a few seconds — fall back to the original
            if (item.mediaUrl && e.currentTarget.src !== item.mediaUrl) e.currentTarget.src = item.mediaUrl;
          }}
          sx={{ width: '100%', height: 110, objectFit: 'cover', cursor: 'pointer', display: 'block' }}
        />
      );
    }
    if (item.MediaType === 'video') {
      return (
        <Box onClick={() => onOpen(item)} sx={{ position: 'relative', cursor: 'pointer', height: 110 }}>
          <Box
            component="video"
            src={item.mediaUrl ? `${item.mediaUrl}#t=0.1` : undefined}
            muted
            playsInline
            preload="metadata"
            sx={{ width: '100%', height: 110, objectFit: 'cover', display: 'block', backgroundColor: alpha(theme.palette.text.primary, 0.85) }}
          />
          <PlayCircleOutline sx={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            fontSize: 40, color: theme.palette.common.white, opacity: 0.9, pointerEvents: 'none',
          }} />
        </Box>
      );
    }
    return (
      <Box sx={{
        height: 110, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 0.5, px: 1,
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
      }}>
        <GraphicEq sx={{ fontSize: 30, color: theme.palette.primary.main }} />
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio controls src={item.mediaUrl || undefined} preload="none" style={{ width: '100%', height: 32 }} />
      </Box>
    );
  };

  return (
    <Box sx={{
      border: `1px solid ${alpha(theme.border.color, 0.35)}`,
      borderRadius: 1.5, overflow: 'hidden',
      backgroundColor: theme.palette.background.paper,
    }}>
      {preview()}
      <Box sx={{ p: 0.75, pt: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" noWrap
            sx={{ fontSize: '0.65rem', flex: 1, minWidth: 0 }}>
            {formatWhen(item.sysCreateDate)} · {formatSize(item.FileSize)}
            {item.sysCreateUser ? ` · ${item.sysCreateUser.split('@')[0]}` : ''}
          </Typography>
          {canEdit && (
            <Tooltip title="Remove (kept in storage archive)">
              <IconButton size="small" onClick={() => onDelete(item)} sx={{ p: 0.25 }}>
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <TextField
          size="small" fullWidth variant="standard" placeholder="Add a caption…"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onBlur={() => {
            const next = caption.trim() || null;
            if (next !== (item.Caption ?? null)) onCaptionSave(item, next);
          }}
          InputProps={{
            readOnly: !canEdit,
            disableUnderline: true,
            sx: { fontSize: '0.72rem' },
          }}
        />
      </Box>
    </Box>
  );
};
